import classNames from 'classnames';
import $ from 'jquery';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import isEqual from 'react-fast-compare';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';

// Exposed in case LegacySelect2 users need to override behavior for the select2-removed event
const SELECT2_REMOVED = 'select2-removed';

export interface LegacySelect2Result {
  id: string;
  text: string;
}

export interface LegacySelect2Query {
  element: JQuery;
  term: string;
  page: number;
}

export type LegacySelect2ChangeEvent = JQueryEventObject & {val: string};

export interface LegacySelect2Props {
  placeholder?: string;
  className?: string;
  options?: Array<{id: string | number | null; text: string}>;
  value?: string | number | null;
  disabled?: boolean;
  disableInput?: boolean;
  allowClear?: boolean;
  large?: boolean;
  minimumInputLength?: number;
  normalCase: boolean;
  width: string;
  multiple?: boolean;
  matcher?: (term: string, text: string, option: unknown) => boolean;
  formatInputTooShort?: (term: string, minLength: number) => string;
  createSearchChoice?: (term: string, data: unknown) => unknown;
  createSearchChoicePosition?: 'top' | 'bottom';
  ajax?: {};
  onChange?: (e: LegacySelect2ChangeEvent) => void;
  name?: string;
  defaultValue?: string;
  formatSelection?: (result: LegacySelect2Result) => string;
  initSelection?: (element: JQuery, callback: (result: LegacySelect2Result) => void) => void;
  formatResult?: (
    object: LegacySelect2Result,
    container: JQuery,
    query: unknown,
    escapeMarkup: (markup: string) => string
  ) => string;
  data?: unknown;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * Legacy component, please use SelectDropdown.tsx instead.
 * @deprecated - Use ui-library/dropdowns/SelectDropdown.tsx instead
 */
class LegacySelect2 extends React.Component<LegacySelect2Props> {
  static propTypes = {
    name: PropTypes.string, // the field name for the INPUT
    large: PropTypes.bool,
    allowClear: PropTypes.bool.isRequired,
    className: PropTypes.string,
    createSearchChoice: PropTypes.func,
    createSearchChoicePosition: PropTypes.oneOf(['top', 'bottom']),
    disabled: PropTypes.bool.isRequired,
    // Make selection read-only, but do not disable the associated hidden input field, which would prevent it
    // from being sent during form submission.
    disableInput: PropTypes.bool,
    formatSelection: PropTypes.func,
    normalCase: PropTypes.bool.isRequired,
    placeholder: PropTypes.string.isRequired,
    multiple: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    defaultValue: PropTypes.string,
    onChange: PropTypes.func,
    width: PropTypes.string.isRequired,
    matcher: PropTypes.func,
    initSelection: PropTypes.func,
    formatResult: PropTypes.func,
    minimumInputLength: PropTypes.number,
    formatInputTooShort: PropTypes.func,
    options: (props: LegacySelect2Props, ...args: [string, string, string, string]) => {
      if (props.ajax == null) {
        return PropTypes.array.isRequired(props, ...args);
      } else if (props.options != null) {
        return new Error('Did not expect "options" prop if "ajax" passed.');
      } else {
        return null;
      }
    },
    ajax: (props: LegacySelect2Props, ...args: [string, string, string, string]) => {
      if (props.options == null) {
        return PropTypes.object.isRequired(props, ...args);
      } else if (props.ajax != null) {
        return new Error('Did not expect "ajax" prop if "options" passed.');
      } else {
        return null;
      }
    },
  };

  static SELECT2_EVENT_KEYS = {
    SELECT2_REMOVED,
  };

  static defaultProps = {
    large: false,
    allowClear: false,
    disabled: false,
    normalCase: false,
    width: '100%',
    placeholder: '',
  };

  inputRef = React.createRef<HTMLInputElement>();
  ghostDivRef = React.createRef<HTMLDivElement>();

  componentDidMount(): void {
    $(ReactDOM.findDOMNode(this.inputRef.current)!).select2({
      containerCssClass: classNames(this.props.className, {
        'select2-container--large': this.props.large,
        'select2-container--normalCase': this.props.normalCase,
      }),
      allowClear: this.props.allowClear,
      createSearchChoice: this.props.createSearchChoice,
      // This property was removed in the version that our select2 types are for
      // @ts-expect-error
      createSearchChoicePosition: this.props.createSearchChoicePosition,
      formatSelection: this.props.formatSelection,
      width: this.props.width,
      placeholder: this.props.placeholder,
      data: this.props.options ? this.dataFunc : undefined,
      multiple: this.props.multiple,
      matcher: this.props.matcher,
      ajax: this.props.ajax,
      initSelection: this.props.initSelection,
      formatResult: this.props.formatResult,
      minimumInputLength: this.props.minimumInputLength,
      formatInputTooShort: this.props.formatInputTooShort,
      disabled: this.props.disabled || this.props.disableInput,
    });
    $(ReactDOM.findDOMNode(this.ghostDivRef.current)!).remove();
    // When the component first mounts, it's possible that the caller has specified defaultValue
    // (indicating it's an uncontrolled component) or value (controlled component).
    // See http://facebook.github.io/react/docs/forms.html
    //
    // Note: we check for presence of value in @props instead of @props.value in [null, undefined] since
    // it is almost definitely the case that the caller was trying to specify a controlled input
    // that doesn't have a selected item.
    // eslint-disable-next-line benchlint/no-in-operator
    if (!('value' in this.props)) {
      $(ReactDOM.findDOMNode(this.inputRef.current)!).select2('val', this.props.defaultValue);
    } else {
      this.syncValue(false);
      $(ReactDOM.findDOMNode(this.inputRef.current)!).on('select2-selecting.select2', this.handleChange);
      $(ReactDOM.findDOMNode(this.inputRef.current)!).on('select2-loaded.select2', () => {
        this.syncValue(false);
      });
      // NOTE We should really be listening to select2-removing, as e.preventDefault below doesn't work with
      // removed, but v3.5.2 isn't properly firing the removing event.
      $(ReactDOM.findDOMNode(this.inputRef.current)!).on(
        `${SELECT2_REMOVED}select2 removed.select2`,
        this.handleChange
      );
    }
  }

  shouldComponentUpdate(nextProps: LegacySelect2Props): boolean {
    return (
      nextProps.value !== this.props.value ||
      !isEqual(
        nextProps.data,
        this.props.data ||
          nextProps.disabled !== this.props.disabled ||
          nextProps.disableInput !== this.props.disableInput ||
          nextProps.placeholder !== this.props.placeholder
      )
    );
  }

  componentDidUpdate(prevProps: LegacySelect2Props): void {
    if (this.props.value !== undefined) {
      this.syncValue(prevProps.value !== this.props.value);
    }

    if (prevProps.placeholder !== this.props.placeholder) {
      $(ReactDOM.findDOMNode(this.inputRef.current)!)
        .data('placeholder', this.props.placeholder)
        .data('select2')
        .setPlaceholder();
    }
  }

  componentWillUnmount(): void {
    $(ReactDOM.findDOMNode(this.inputRef.current)!).off('.select2').select2('destroy');
  }

  getValue(): string {
    return $(ReactDOM.findDOMNode(this.inputRef.current)!).select2('val');
  }

  handleChange = (e: LegacySelect2ChangeEvent): void => {
    // Always prevent default to stop change, rely on props update instead.
    e.preventDefault();
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(e);
    }
    if (!this.props.multiple) {
      $(ReactDOM.findDOMNode(this.inputRef.current)!).select2('close');
    }
  };

  // Using a function for data apparently lets select2 pull in the latest @props.options each time.
  dataFunc = (): {results: LegacySelect2Props['options']} => ({results: this.props.options});

  // Syncs the value from props, and optionally triggers a change event on the INPUT DOM node.
  syncValue = (triggerChange: boolean): void => {
    $(ReactDOM.findDOMNode(this.inputRef.current)!).select2('val', this.props.value, triggerChange);
  };

  render(): JSX.Element {
    return (
      <div
        {...getTestProps(
          this.props.dataTestAttrs ? this.props.dataTestAttrs : {component: 'LegacySelect2', element: 'root'}
        )}
      >
        {
          // Note: We create this div so that we can eventually replace it with the select2 object.
          // This allows us to maintain the position of the input component so that on prop update
          // React does not error.
          <div ref={this.ghostDivRef} />
        }
        <input
          ref={this.inputRef}
          type="hidden"
          disabled={this.props.disabled}
          readOnly={this.props.disableInput}
          name={this.props.name}
        />
      </div>
    );
  }
}

export default LegacySelect2;
